import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import LegacyPatientImportSummary from "./LegacyPatientImportSummary";
import type { LegacyReviewValues } from "../../../utils/legacyReviewForm";

interface Props {
  opened: boolean;
  caseNumber: string;
  revision: number;
  values: LegacyReviewValues;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
  error?: string;
  importBlocked?: boolean;
}

export default function LegacyImportConfirmation({
  opened,
  caseNumber,
  revision,
  values,
  pending,
  onClose,
  onConfirm,
  error,
  importBlocked,
}: Props) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Import ${caseNumber}?`}
      centered
      closeOnClickOutside={false}
      closeOnEscape={!pending}
      withCloseButton={!pending}
    >
      <Stack gap="md">
        <Text>
          Import saved revision {revision} as a completed historical case with
          the original final report and the patient link shown below. Evidenced
          records are created only where requested.
        </Text>
        <LegacyPatientImportSummary values={values} />
        {importBlocked && (
          <Alert color="orange" title="Case number now exists">
            Another case now uses this number. Keep reviewing the archive and
            open the existing case before continuing. Your saved review is
            retained.
          </Alert>
        )}
        <Alert color="orange">
          Confirm only after reviewing all source documents and the saved draft.
          This import cannot be undone here. Amendments, addenda, corrections,
          and new report generation are unavailable for imported cases.
        </Alert>
        {error && (
          <Alert color="red" title="Import not completed">
            {error}
          </Alert>
        )}
        <Group justify="space-between">
          <Button
            variant="default"
            onClick={onClose}
            disabled={pending}
            data-autofocus
          >
            Keep reviewing
          </Button>
          <Button
            onClick={onConfirm}
            loading={pending}
            disabled={importBlocked}
          >
            Confirm import
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
